import { ElLoading } from "element-plus";
import { watch } from "vue";
import { createRouter, createWebHistory, RouteLocationNormalized, RouterOptions } from "vue-router";

import DummyRouterView from "@/components/structure/DummyRouterView.vue";
import ActionLogsTabsPage from "@/pages/games/GameDisplay/children/actionlogs/ActionLogsTabsPage.vue";
import useAuthentication from "@/hooks/use-authentication";
import usePermissions from "@/hooks/use-permissions";
import ForbiddenPage from "@/pages/Forbidden/ForbiddenPage.vue";
import GameHomePage from "@/pages/games/GameDisplay/children/home/GameHomePage.vue";
import PartialPublishPage from "@/pages/games/GameDisplay/children/publish/PartialPublishPage.vue";
import PublishPage from "@/pages/games/GameDisplay/children/publish/PublishPage.vue";
import AddDevicePage from "@/pages/games/GameDisplay/children/testers/AddDevice/AddDevicePage.vue";
import GameDisplayPage from "@/pages/games/GameDisplay/GameDisplayPage.vue";
import GameImportPage from "@/pages/games/GameImport/GameImportPage.vue";
import GamesListPage from "@/pages/games/GamesList/GamesListPage.vue";
import GamesPage from "@/pages/games/GamesPage.vue";
import LoginPage from "@/pages/Login/LoginPage.vue";
import LogoutPage from "@/pages/Logout/LogoutPage.vue";
import MaintenancePage from "@/pages/Maintenance/MaintenancePage.vue";
import NotFoundPage from "@/pages/NotFound/NotFoundPage.vue";
import NotificationsPage from "@/pages/Notifications/NotificationsPage.vue";
import RootPage from "@/pages/Root/RootPage.vue";

import abtestsRoutes from "./abtests";
import actionLogsRoutes from "./actionlogs";
import classesRoutes from "./classes";
import configurationsRoutes from "./configurations";
import featuresRoutes from "./features";
import iapRoutes from "./iap";
import layersRoutes from "./layers";
import links from "./links";
import offersAndStoreRoutes from "./offersAndStore";
import sandboxRoutes from "./sandboxes";
import segmentsRoutes from "./segments";
import simulatorRoutes from "./simulator";
import userInspectorRoutes from "./userInspector";
import testDevices from "./test-devices";

const { canWriteGames, canReadGames, canWriteVersions, canWriteTesters } = usePermissions();

const routerConfig: RouterOptions = {
  history: createWebHistory("/"),
  routes: [
    {
      path: "/login",
      name: links.login,
      component: LoginPage,
      props: true,
      meta: {
        title: "Login",
        noAuthRequired: true,
      },
    },
    {
      path: "/unauthorized",
      name: links.unauthorized,
      component: LoginPage,
      props: (route) => ({ ...route.params, unauthorized: true }),
      meta: {
        title: "Unauthorized",
        noAuthRequired: true,
      },
    },
    {
      path: "/maintenance",
      name: links.maintenance,
      component: MaintenancePage,
      props: true,
      meta: {
        title: "Sorry for the inconvenience 🤷",
        noAuthRequired: true,
      },
    },
    {
      path: "/forbidden",
      name: links.forbidden,
      component: ForbiddenPage,
      props: true,
      meta: {
        title: "Forbidden",
        noAuthRequired: true,
      },
    },
    {
      path: "/logout",
      component: LogoutPage,
      meta: {
        noAuthRequired: true,
      },
    },
    {
      path: "/release-notes",
      name: links.releaseNotes,
      component: NotificationsPage,
      props: true,
      meta: {
        title: "Release notes",
      },
    },
    {
      path: "/add-device",
      name: links.addDevice,
      component: AddDevicePage,
      props: true,
      meta: {
        title: "Add device",
        permission: canWriteTesters,
      },
    },
    {
      path: "/implicit/callback",
      name: links.loginRedirect,
      component: NotFoundPage,
    },
    {
      path: "/games",
      component: RootPage,
      props: true,
      meta: {
        title: "Game",
        permission: canReadGames,
      },
      children: [
        {
          path: "",
          component: GamesPage,
          props: true,
          meta: {
            title: "Game",
            permission: canReadGames,
          },
          children: [
            {
              path: "import",
              name: links.games.import,
              component: GameImportPage,
              meta: {
                title: "Import game",
                permission: canWriteGames,
              },
            },
            {
              path: ":gameId/:versionId",
              component: GameDisplayPage,
              props: true,
              children: [
                {
                  path: "",
                  name: links.games.home,
                  component: GameHomePage,
                  props: true,
                  meta: {
                    permission: canReadGames,
                  },
                },
                {
                  path: "publish",
                  name: links.games.publish,
                  component: PublishPage,
                  props: true,
                  meta: {
                    title: "Publish your game",
                    permission: canWriteVersions,
                  },
                },
                {
                  path: "partial-publish",
                  name: links.games.partialPublish,
                  component: PartialPublishPage,
                  props: true,
                  meta: {
                    title: "Partial publish",
                    permission: canWriteVersions,
                  },
                },
                {
                  path: "classes",
                  component: DummyRouterView,
                  props: true,
                  children: classesRoutes,
                },
                {
                  path: "features",
                  component: DummyRouterView,
                  props: true,
                  children: featuresRoutes,
                },
                {
                  path: "segments",
                  component: DummyRouterView,
                  props: true,
                  children: segmentsRoutes,
                },
                {
                  path: "test-devices",
                  component: DummyRouterView,
                  props: true,
                  children: testDevices,
                },
                {
                  path: "sandboxes",
                  component: DummyRouterView,
                  props: true,
                  children: sandboxRoutes,
                },
                {
                  path: "configurations",
                  component: DummyRouterView,
                  props: true,
                  children: configurationsRoutes,
                },
                {
                  path: "simulator",
                  component: DummyRouterView,
                  props: true,
                  children: simulatorRoutes,
                },
                {
                  path: "abtests",
                  component: DummyRouterView,
                  props: true,
                  children: abtestsRoutes,
                },
                {
                  path: "user-inspector",
                  component: DummyRouterView,
                  props: true,
                  children: userInspectorRoutes,
                },
                {
                  path: "layers",
                  component: DummyRouterView,
                  props: true,
                  children: layersRoutes,
                },
                {
                  path: "action-logs",
                  component: ActionLogsTabsPage,
                  props: true,
                  children: actionLogsRoutes,
                },
                {
                  path: "iap",
                  component: DummyRouterView,
                  props: true,
                  children: iapRoutes,
                },
                {
                  path: "offersAndStore",
                  component: DummyRouterView,
                  props: true,
                  children: offersAndStoreRoutes,
                },
              ],
            },
            {
              path: "",
              name: links.games.list,
              component: GamesListPage,
              meta: {
                title: "Games list",
                permission: canReadGames,
              },
            },
          ],
        },
      ],
    },
    { path: "/", name: "home", redirect: "/games" },
    {
      path: "/404",
      name: links.notFound,
      component: NotFoundPage,
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: NotFoundPage,
    },
  ],
  // scrollBehavior(to, from, savedPosition) {
  //   if (to.hash) {
  //     return new Promise((resolve) => {
  //       setTimeout(() => {
  //         resolve({
  //           selector: to.hash,
  //           behavior: "smooth",
  //         });
  //       }, 500);
  //     });
  //   }
  //   if (savedPosition) {
  //     return new Promise((resolve) => {
  //       setTimeout(() => {
  //         resolve(savedPosition);
  //       }, 200);
  //     });
  //   }
  //   return null;
  // },
};

const router = createRouter(routerConfig);

let loadingInstance: ReturnType<typeof ElLoading.service> | null = null;

/**
 * Each time we change route, we check if the user is still authenticated.
 * Apply the authGuard for each route that doesn't specify the "noAuthRequired" meta option
 */
router.beforeEach((to: RouteLocationNormalized) => {
  const { loading, routeAuthGuard, isAuthenticated } = useAuthentication();
  const authGuard = routeAuthGuard({
    router,
    loginRouteName: links.login,
    forbiddenRouteName: links.forbidden,
    authRedirectRouteName: links.loginRedirect,
  });

  watch(isAuthenticated, (isAuth) => {
    if (loadingInstance && isAuth) {
      loadingInstance.close();
    }
  });

  watch(loading, (watchedLoading) => {
    if (watchedLoading && !isAuthenticated.value && !loadingInstance) {
      loadingInstance = ElLoading.service({
        fullscreen: true,
        lock: true,
        text: "Login in progress. Please wait a bit...",
      });
    } else if (loadingInstance) {
      loadingInstance.close();
    }
  });

  return authGuard(to);
});

/**
 * Scroll the window to top between each pages...
 * ...unless preventScrollTop is true in the route meta infos.
 */
router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  if (!(to?.meta?.preventScrollTop && from?.meta?.preventScrollTop)) {
    window.scrollTo(0, 0);
  }
});

/**
 * Set the document title from route's meta information.
 */
router.beforeEach((to: RouteLocationNormalized) => {
  const title = "VoodooTune";
  document.title = to?.meta?.title ? `${title} | ${to?.meta?.title}` : title;
});

export default router;
